import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Select } from "@/components/ui/select/select";
import { useInstallations } from "@/features/installations/use-installations";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowFloor } from "../../types/location-map";

import { useCreateFloor } from "../../hooks/useMapEntityCreates";
import { useNameBackedExternalIdSync } from "../../hooks/useNameBackedExternalId";
import { getRefId } from "../../utils/location/installation-ids";
import { hasDuplicateExternalId } from "../../utils/shared/external-id";
import { createAtLocationDescription } from "./shared/create-at-location-description";

const DUPLICATE_EXTERNAL_ID_MESSAGE =
	"A floor with this External ID already exists for this installation.";

export type CreateFloorFormProps = {
	floors?: ShadowFloor[];
	locationId: string;
	locationName?: string;
	onClose: () => void;
	onCreated?: (floorId: string) => void;
};

type CreateFloorFormValues = {
	description: string;
	externalId: string;
	installation: string;
	name: string;
};

export const CreateFloorForm = ({
	floors = [],
	locationId,
	locationName,
	onClose,
	onCreated,
}: CreateFloorFormProps) => {
	const createFloor = useCreateFloor();
	const { data: installations = [], isLoading: isLoadingInstallations } =
		useInstallations();
	const [submitError, setSubmitError] = useState<null | string>(null);

	const locationInstallations = useMemo(
		() =>
			installations.filter(
				(installation) => getRefId(installation.location) === locationId,
			),
		[installations, locationId],
	);

	const installationItems = useMemo(
		() =>
			locationInstallations.map((installation) => ({
				id: installation.id,
				label: installation.external.id,
			})),
		[locationInstallations],
	);

	const defaultValues = useMemo(
		(): CreateFloorFormValues => ({
			description: "",
			externalId: "",
			installation: locationInstallations[0]?.id ?? "",
			name: "",
		}),
		[locationInstallations],
	);

	const {
		control,
		formState: { errors, isValid },
		getValues,
		handleSubmit,
		setError,
		setValue,
		trigger,
	} = useForm<CreateFloorFormValues>({
		defaultValues,
		mode: "onChange",
	});

	const { syncExternalIdChange, syncNameChange } =
		useNameBackedExternalIdSync(setValue);

	const selectedInstallationId = useWatch({ control, name: "installation" });

	useEffect(() => {
		const current = getValues("installation");
		if (!current && locationInstallations[0]?.id) {
			setValue("installation", locationInstallations[0].id, {
				shouldValidate: true,
			});
		}
	}, [getValues, locationInstallations, setValue]);

	useEffect(() => {
		if (!getValues("externalId").trim()) return;
		void trigger("externalId");
	}, [getValues, selectedInstallationId, trigger]);

	const validateExternalId = (value: string): string | true => {
		const trimmed = value.trim();
		if (!trimmed) return true;
		const installationId = getValues("installation").trim();
		if (hasDuplicateExternalId(trimmed, installationId, floors)) {
			return DUPLICATE_EXTERNAL_ID_MESSAGE;
		}
		return true;
	};

	const canSubmit =
		isValid && locationInstallations.length > 0 && !createFloor.isPending;

	const onSubmit = handleSubmit((data) => {
		const installation = data.installation.trim();
		if (!installation) {
			setError("installation", { message: "Installation is required" });
			return;
		}

		if (hasDuplicateExternalId(data.externalId, installation, floors)) {
			setError("externalId", { message: DUPLICATE_EXTERNAL_ID_MESSAGE });
			return;
		}

		setSubmitError(null);
		createFloor.mutate(
			{
				description: data.description.trim() || undefined,
				externalId: data.externalId.trim(),
				installation,
				name: data.name.trim(),
			},
			{
				onError: () => {
					setSubmitError("Could not create floor.");
				},
				onSuccess: (created) => {
					onCreated?.(created.id);
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description={
				locationInstallations.length === 0
					? createAtLocationDescription(locationName, {
							fallback:
								"Create an installation for this location before adding a floor.",
							withLocation: (location) => (
								<>
									Create an installation for {location} before adding a floor.
								</>
							),
						})
					: createAtLocationDescription(locationName, {
							fallback:
								"Add a floor under an installation at this location. A floor plan can be placed on the map after it is created.",
							withLocation: (location) => (
								<>
									Add a floor under an installation at {location}. A floor plan
									can be placed on the map after it is created.
								</>
							),
						})
			}
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			submitDisabled={!canSubmit}
			submitText={createFloor.isPending ? "Creating…" : "Create floor"}
			title="Create floor"
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							errorMessage={errors.name?.message}
							isInvalid={Boolean(errors.name)}
							isRequired
							label="Name"
							onBlur={field.onBlur}
							onChange={(value) => syncNameChange(value, field.onChange)}
							placeholder="Level 1"
							ref={field.ref}
							value={field.value}
						/>
					)}
					rules={{
						required: "Floor name is required",
						validate: (value) =>
							value.trim().length > 0 || "Floor name is required",
					}}
				/>
				<Controller
					control={control}
					name="externalId"
					render={({ field }) => (
						<Input
							errorMessage={errors.externalId?.message}
							hint="Auto-filled from the name; edit anytime."
							isInvalid={Boolean(errors.externalId)}
							isRequired
							label="External ID"
							onBlur={field.onBlur}
							onChange={(value) => {
								syncExternalIdChange(value, field.onChange);
								void trigger("externalId");
							}}
							placeholder="floor-level-1"
							ref={field.ref}
							value={field.value}
						/>
					)}
					rules={{
						required: "External ID is required",
						validate: validateExternalId,
					}}
				/>
				<Controller
					control={control}
					name="installation"
					render={({ field }) => (
						<Select
							errorMessage={errors.installation?.message}
							isDisabled={
								isLoadingInstallations || locationInstallations.length === 0
							}
							isInvalid={Boolean(errors.installation)}
							isRequired
							items={installationItems}
							label="Installation"
							onChange={(value) => {
								field.onChange(value ? String(value) : "");
							}}
							placeholder={
								isLoadingInstallations
									? "Loading installations…"
									: locationInstallations.length === 0
										? "No installations"
										: "Select installation"
							}
							value={field.value || null}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
					rules={{ required: "Installation is required" }}
				/>
				<Controller
					control={control}
					name="description"
					render={({ field }) => (
						<Input
							label="Description (optional)"
							onBlur={field.onBlur}
							onChange={field.onChange}
							ref={field.ref}
							value={field.value}
						/>
					)}
				/>
				{submitError ? (
					<p className="text-sm text-error-primary">{submitError}</p>
				) : null}
			</Form>
		</GeneralModal>
	);
};
